import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'
import { injectQuery, injectMutation, injectQueryClient } from './query'
import { Team } from './teams.service'
import { queryKeys } from './query-keys'
import { ToastService } from './toast.service'
import { Template, TemplateItem } from './template-editor.component/types/template.types'

export type UpdateTemplateItemsResponse = {
  clientIdToServerIdMapping: Record<string, number>
}

export type TemplateWithoutId = Omit<Template, 'id'>

export type TemplateWithTeam = Template & {
  team: Team
}

@Injectable({
  providedIn: 'root',
})
export class TemplatesService {
  httpClient = inject(HttpClient)
  query = injectQuery()
  mutation = injectMutation()
  queryClient = injectQueryClient()
  toastService = inject(ToastService)
  router = inject(Router)

  getTemplateById(id: number, receiveUpdates = true) {
    return this.query({
      queryKey: queryKeys.templates.byId(id),
      queryFn: () => this.httpClient.get<TemplateWithTeam>(`/api/templates/${id}`),
      refetchOnMount: receiveUpdates,
      refetchOnReconnect: receiveUpdates,
      refetchOnWindowFocus: receiveUpdates,
    }).result$
  }

  getTemplateModels() {
    return this.query({
      queryKey: queryKeys.templates.models.own,
      queryFn: () => this.httpClient.get<TemplateWithTeam[]>('/api/templates/models'),
    }).result$
  }

  getTemplateModelsAdmin(teamId: number | null = null) {
    const url = teamId !== null ? `/api/templates/models/support?teamId=${teamId}` : '/api/templates/models/support'
    return this.query({
      queryKey: teamId !== null ? queryKeys.templates.models.byTeam(teamId) : queryKeys.templates.models.support,
      queryFn: () => this.httpClient.get<TemplateWithTeam[]>(url),
    }).result$
  }

  getTemplateItems(meetingId: number) {
    return this.query<TemplateItem[]>({
      queryKey: queryKeys.templates.byMeetingId(meetingId),
      queryFn: () => this.httpClient.get<TemplateItem[]>(`/api/meetings/${meetingId}/template-items`),
      refetchInterval: 10000,
    }).result$
  }

  createTemplate() {
    return this.mutation({
      mutationFn: (template: TemplateWithoutId) => this.httpClient.post<Template>('/api/templates', template),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.base })
        this.toastService.show('TEMPLATES.CREATED')
      },
      onError: (error) => {
        this.toastService.show('TEMPLATES.CREATE_FAILED')
      },
    })
  }

  updateTemplate() {
    return this.mutation({
      mutationFn: (template: Template) => this.httpClient.put<Template>(`/api/templates/${template.id}`, template),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.base })
        this.toastService.show('TEMPLATES.UPDATED')
      },
      onError: (error) => {
        this.toastService.show('TEMPLATES.UPDATE_FAILED')
      },
    })
  }

  updateTemplateItems() {
    return this.mutation({
      mutationFn: ({ meetingId, items }: { meetingId: number; items: TemplateItem[] }) =>
        this.httpClient.put<UpdateTemplateItemsResponse>(`/api/meetings/${meetingId}/template-items`, { items }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byMeetingId(params.meetingId) })
        this.toastService.show('TEMPLATES.TEMPLATE_SAVED')
      },
      onError: () => {
        this.toastService.show('TEMPLATES.SAVE_FAILED')
      },
    })
  }

  loadTemplate() {
    return this.mutation({
      mutationFn: ({ meetingId, templateId }: { meetingId: number; templateId: number }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/load-template`, { templateId }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byMeetingId(params.meetingId) })
        this.toastService.show('TEMPLATES.TEMPLATE_LOADED')
      },
      onError: () => {
        this.toastService.show('TEMPLATES.LOAD_FAILED')
      },
    })
  }

  deleteTemplate() {
    return this.mutation({
      mutationFn: (templateId: number) => this.httpClient.delete(`/api/templates/${templateId}`),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.base })
        this.toastService.show('TEMPLATES.DELETED')
      },
      onError: (error) => {
        this.toastService.show('TEMPLATES.DELETE_FAILED')
      },
    })
  }

  saveMeetingTemplateAsModel() {
    return this.mutation({
      mutationFn: ({ name, items }: { teamId: number; name: string; items: TemplateItem[] }) =>
        this.httpClient.post<Template>('/api/templates/save-as', {
          name,
          items,
        }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({
          queryKey: queryKeys.templates.models.byTeam(params.teamId),
          refetchType: 'all',
        })
        this.queryClient.invalidateQueries({
          queryKey: queryKeys.templates.models.base,
          refetchType: 'all',
        })
        this.toastService.show('TEMPLATES.SAVE_AS_SUCCESS')
      },
      onError: (error) => {
        this.toastService.show('TEMPLATES.SAVE_AS_FAILED')
      },
    })
  }

  exportTemplate() {
    return this.mutation({
      mutationFn: ({ templateId }: { templateId: number; templateName: string }) =>
        this.httpClient.get<{ name: string; teamId: number; items: TemplateItem[] }>(`/api/templates/${templateId}/export`),
      onSuccess: (data, params) => {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${params.templateName}-template.json`
        link.click()
        window.URL.revokeObjectURL(url)
        this.toastService.show('TEMPLATES.EXPORT_SUCCESS')
      },
      onError: () => {
        this.toastService.show('TEMPLATES.EXPORT_FAILED')
      },
    })
  }

  importTemplate() {
    return this.mutation({
      mutationFn: (data: { name: string; teamId: number; items: TemplateItem[] }) => this.httpClient.post<Template>('/api/templates/import', data),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.base })
        this.toastService.show('TEMPLATES.IMPORT_SUCCESS')
      },
      onError: (error) => {
        this.toastService.show('TEMPLATES.IMPORT_FAILED')
      },
    })
  }

  ingestTemplate() {
    return this.mutation({
      mutationFn: (data: { name: string; teamId: number; file: File }) => {
        const formData = new FormData()
        formData.append('file', data.file)
        const params = new HttpParams().set('name', data.name).set('teamId', data.teamId.toString())
        return this.httpClient.post<Template>('/api/templates/ingest', formData, { params })
      },
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.base })
        this.toastService.show('TEMPLATES.INGEST_SUCCESS')
      },
      onError: () => {
        this.toastService.show('TEMPLATES.INGEST_FAILED')
      },
    })
  }
}
